/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Latest published posts (not the featured flag).
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArticleIcon from '@mui/icons-material/Article';
import { Link } from '@tanstack/react-router';
import { analytics } from '@/lib/analytics';

interface LatestPost {
  slug: string;
  title: string;
  description?: string;
  headerImage?: string;
  url: string;
  tags?: string[];
  formattedDate?: string;
  readTime?: string;
}

export function HomeFeaturedWriting({ posts }: { posts: LatestPost[] }) {
  const featured = posts.slice(0, 2);

  return (
    <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" justifyContent="space-between" className="mb-6">
          <Box>
            <Typography variant="h4" component="h2" fontWeight="bold">
              <ArticleIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 32 }} />
              Featured Writing
            </Typography>
            <Typography variant="body1" color="text.secondary" className="mt-2">
              Latest posts — architecture, frameworks, and building things
            </Typography>
          </Box>
          <Button component={Link} to="/blog" variant="outlined" size="large">
            View All Articles
          </Button>
        </Stack>

        <Grid container spacing={4}>
          {featured.length > 0 ? (
            featured.map((post) => (
              <Grid size={{ xs: 12, md: featured.length === 1 ? 12 : 6 }} key={post.slug}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {post.headerImage && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={post.headerImage}
                      alt={post.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent className="flex-1">
                    <Stack spacing={2}>
                      {post.tags && post.tags.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          {post.tags.slice(0, 5).map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Stack>
                      )}
                      <Typography variant="h5" fontWeight="bold">
                        {post.title}
                      </Typography>
                      {post.description && (
                        <Typography variant="body2" color="text.secondary">
                          {post.description}
                        </Typography>
                      )}
                      {(post.formattedDate || post.readTime) && (
                        <Typography variant="caption" color="text.secondary">
                          {post.formattedDate}
                          {post.formattedDate && post.readTime && ' • '}
                          {post.readTime}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  <CardActions>
                    <Button
                      component={Link}
                      to={post.url}
                      size="large"
                      fullWidth
                      onClick={() => analytics.contentClicked('blog_post', post.title, post.url)}
                    >
                      Read Article
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Check out the <Link to="/blog">full blog</Link>.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
